import { createZodFormResolver } from '@mews/framework';
import { CommanderFormProps, useDialogFormControls } from '@mews-commander/form';
import { WithAccountId } from '@mews-commander/types';
import { PaymentRefundWithFXFormValues, createPaymentRefundWithFXFormSchema } from '../schema';
import { PaymentRefundWithFXFormField, PaymentRefundNormalizedData } from '../types';
import { useCreatePaymentRefund } from '../../../../queries';
import { useGetPaymentTerminalIdForRefundsSuspense } from '../queries';
import { MewsTerminalFormField } from '../../ProcessPayment/MewsTerminalPayment/schema';
import { PaymentRefundWithFXFormFields } from './PaymentRefundWithFXFormFields';

export const PaymentRefundWithFXForm = ({
    paymentId,
    data,
    accountId,
    ...formProps
}: { paymentId: string; data: PaymentRefundNormalizedData } & CommanderFormProps & WithAccountId) => {
    const {
        refundableValue,
        refundConversionMultiplier,
        propertyCurrencyRefundAmount,
        chargeAmount,
        propertyCurrencyChargeAmount,
        refundMode,
    } = data;

    const schema = createPaymentRefundWithFXFormSchema(refundableValue, propertyCurrencyRefundAmount.value);
    const resolver = createZodFormResolver(schema);
    const createPaymentRefund = useCreatePaymentRefund({ paymentId, accountId });

    const { data: preselectedTerminalId } = useGetPaymentTerminalIdForRefundsSuspense(refundMode);

    const { formRef, onSuccess, onSubmitted, onSubmitting } = formProps;
    const { renderFormContent } = useDialogFormControls<PaymentRefundWithFXFormValues>({
        formRef,
        onSubmit: ({ chargedCurrencyAmount, ...values }) => {
            onSubmitting?.();
            const isPartialRefund = refundableValue !== chargedCurrencyAmount;
            createPaymentRefund.mutate(
                { amount: chargedCurrencyAmount, isPartialRefund, ...values },
                { onSettled: onSubmitted, onSuccess }
            );
        },
        resolver,
        defaultValues: {
            [PaymentRefundWithFXFormField.ChargedCurrencyAmount]: refundableValue,
            [PaymentRefundWithFXFormField.PropertyCurrencyAmount]: propertyCurrencyRefundAmount.value,
            [PaymentRefundWithFXFormField.Notes]: '',
            [PaymentRefundWithFXFormField.RefundMode]: refundMode,
            [MewsTerminalFormField.PaymentTerminalId]: preselectedTerminalId,
        },
    });

    return renderFormContent(
        <PaymentRefundWithFXFormFields
            chargedCurrencyCode={chargeAmount.currencyCode}
            propertyCurrencyCode={propertyCurrencyChargeAmount.currencyCode}
            refundConversionMultiplier={refundConversionMultiplier}
        />
    );
};
